import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Paper,
    Container,
    CircularProgress,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Button,
    IconButton,
    Chip,
    Tooltip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Card,
    CardContent,
    useTheme,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import EmailIcon from '@mui/icons-material/Email';
import SmsIcon from '@mui/icons-material/Sms';
import {
    noticesService,
    NoticeFilters,
    NoticeListItem,
    NoticeType,
    NoticeStatus,
    NoticePagination,
    NoticeSortBy,
} from '../../services/notices-service';
import { formatFriendlyDate } from '../../utils/dateUtils';
import NoticeModal from './NoticeModal';

// Notice type color mapping
const getNoticeTypeColor = (notice: NoticeListItem, isDark: boolean) => {
    // Handle Consent Notices
    if (notice.sourceType === 'CONSENT_NOTICE') {
        if (isDark) {
            return { bg: '#1E3A4A', color: '#81C784', border: '#4CAF50' };
        } else {
            return { bg: '#E8F5E9', color: '#2E7D32', border: '#4CAF50' };
        }
    }

    const type = notice.NoticeType;
    if (!type) {
        // Fallback for undefined type
        if (isDark) {
            return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
        } else {
            return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
        }
    }

    if (isDark) {
        switch (type) {
            case 'BREACH_NOTICE':
                return { bg: '#4A1E1E', color: '#E57373', border: '#DC3545' };
            case 'ERASURE_NOTICE':
                return { bg: '#1B3A4A', color: '#64B5F6', border: '#2196F3' };
            case 'INFORMATIONAL_NOTICE':
                return { bg: '#2C3E1E', color: '#81C784', border: '#4CAF50' };
            default:
                return { bg: '#2C2C2C', color: '#9E9E9E', border: '#6C757D' };
        }
    } else {
        switch (type) {
            case 'BREACH_NOTICE':
                return { bg: '#F8D7DA', color: '#721C24', border: '#DC3545' };
            case 'ERASURE_NOTICE':
                return { bg: '#D1ECF1', color: '#0C5460', border: '#17A2B8' };
            case 'INFORMATIONAL_NOTICE':
                return { bg: '#D4EDDA', color: '#155724', border: '#28A745' };
            default:
                return { bg: '#E2E3E5', color: '#383D41', border: '#6C757D' };
        }
    }
};

// Notice type label
const getNoticeTypeLabel = (notice: NoticeListItem): string => {
    // Handle Consent Notices
    if (notice.sourceType === 'CONSENT_NOTICE') {
        return 'Consent Notice';
    }

    const type = notice.NoticeType;
    if (!type) {
        return 'Unknown';
    }

    switch (type) {
        case 'BREACH_NOTICE':
            return 'Breach';
        case 'ERASURE_NOTICE':
            return 'Erasure';
        case 'INFORMATIONAL_NOTICE':
            return 'Informational';
        default:
            return type;
    }
};

const Notices: React.FC = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    // State management
    const [notices, setNotices] = useState<NoticeListItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Pagination state
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(20);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [hasPrevPage, setHasPrevPage] = useState(false);

    // Filter state
    const [filters, setFilters] = useState<NoticeFilters>({});
    const [sortBy, setSortBy] = useState<NoticeSortBy>('newest');
    const [searchQuery, setSearchQuery] = useState('');

    // UI state
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
    const [noticeModalOpen, setNoticeModalOpen] = useState(false);
    const [noticeToEdit, setNoticeToEdit] = useState<number | null>(null);

    // Load notices
    const loadNotices = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const pagination: NoticePagination = {
                page: page + 1, // Backend uses 1-based pagination
                pageSize
            };

            const filtersWithSearch: NoticeFilters = {
                ...filters,
                search: searchQuery || undefined
            };

            const result = await noticesService.getNoticesList(
                filtersWithSearch,
                pagination,
                sortBy
            );

            setNotices(result.notices);
            setTotalCount(result.totalCount);
            setTotalPages(result.totalPages);
            setHasNextPage(result.hasNextPage);
            setHasPrevPage(result.hasPrevPage);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load notices');
            console.error('Error loading notices:', err);
        } finally {
            setLoading(false);
        }
    }, [page, pageSize, filters, sortBy, searchQuery]);

    useEffect(() => {
        loadNotices();
    }, [loadNotices]);

    // Handle pagination
    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setPageSize(parseInt(event.target.value, 10));
        setPage(0);
    };

    // Handle filters
    const handleFilterChange = (key: keyof NoticeFilters, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value === '' ? undefined : value
        }));
        setPage(0);
    };

    const clearFilters = () => {
        setFilters({});
        setSearchQuery('');
        setPage(0);
    };

    const hasActiveFilters = () => {
        return Object.keys(filters).length > 0;
    };

    // Handle search
    const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setPage(0);
    };



    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Header */}
            <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h4" component="h1">
                    Notices
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => {
                        setNoticeToEdit(null);
                        setNoticeModalOpen(true);
                    }}
                >
                    Create Notice
                </Button>
            </Box>

            {/* Error Alert */}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Search and Actions */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    {/* Search */}
                    <TextField
                        placeholder="Search notices..."
                        value={searchQuery}
                        onChange={handleSearchChange}
                        size="small"
                        InputProps={{
                            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
                        }}
                        sx={{ flexGrow: 1, minWidth: 200 }}
                    />

                    {/* Sort */}
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                        <InputLabel>Sort By</InputLabel>
                        <Select
                            value={sortBy}
                            label="Sort By"
                            onChange={(e) => setSortBy(e.target.value as NoticeSortBy)}
                        >
                            <MenuItem value="newest">Newest</MenuItem>
                            <MenuItem value="oldest">Oldest</MenuItem>
                            <MenuItem value="effectiveDate">Effective Date</MenuItem>
                            <MenuItem value="assignedDate">Assigned Date</MenuItem>
                            <MenuItem value="title">Title</MenuItem>
                            <MenuItem value="type">Type</MenuItem>
                        </Select>
                    </FormControl>

                    {/* Filter Button */}
                    <IconButton
                        onClick={() => setIsFilterModalOpen(true)}
                        title="Filters"
                        sx={{
                            position: 'relative',
                            '&::after': hasActiveFilters() ? {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                right: 0,
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                bgcolor: 'primary.main',
                                border: `2px solid ${theme.palette.background.paper}`,
                            } : {}
                        }}
                    >
                        <FilterListIcon />
                    </IconButton>

                    {/* Refresh */}
                    <IconButton onClick={loadNotices} title="Refresh">
                        <RefreshIcon />
                    </IconButton>
                </Box>
            </Paper>

            {/* Table */}
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell>Type</TableCell>
                            <TableCell>Title</TableCell>
                            {/* Show assignment columns when filtering by DP or assignment fields */}
                            {(filters.dpId || filters.assignmentStatus || filters.isAcknowledged !== undefined || filters.emailSent !== undefined || filters.smsSent !== undefined) ? (
                                <>
                                    <TableCell>Data Principal</TableCell>
                                    <TableCell>Assignment Status</TableCell>
                                    <TableCell>Acknowledged</TableCell>
                                    <TableCell>Effective Date</TableCell>
                                    <TableCell>Notifications</TableCell>
                                </>
                            ) : (
                                <>
                                    <TableCell>Assignments</TableCell>
                                    <TableCell>Notice Status</TableCell>
                                </>
                            )}
                            <TableCell>Created</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={(filters.dpId || filters.assignmentStatus || filters.isAcknowledged !== undefined || filters.emailSent !== undefined || filters.smsSent !== undefined) ? 8 : 6} align="center" sx={{ py: 4 }}>
                                    <CircularProgress />
                                </TableCell>
                            </TableRow>
                        ) : notices.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={(filters.dpId || filters.assignmentStatus || filters.isAcknowledged !== undefined || filters.emailSent !== undefined || filters.smsSent !== undefined) ? 8 : 6} align="center" sx={{ py: 6 }}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                                        <Typography variant="h6" color="text.secondary">
                                            No notices found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 500, textAlign: 'center' }}>
                                            {hasActiveFilters() || searchQuery.length > 0
                                                ? 'No notices match your current filters. Try adjusting your search criteria or clear the filters to see all notices.'
                                                : 'You haven\'t created any notices yet. Notices are used to communicate important information to Data Principals, such as data breaches, erasure notifications, or other informational updates.'}
                                        </Typography>
                                        {!hasActiveFilters() && searchQuery.length === 0 && (
                                            <Button
                                                variant="contained"
                                                startIcon={<AddIcon />}
                                                onClick={() => {
                                                    setNoticeToEdit(null);
                                                    setNoticeModalOpen(true);
                                                }}
                                                sx={{ mt: 1 }}
                                            >
                                                Create Your First Notice
                                            </Button>
                                        )}
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ) : (
                            notices.map((notice) => {
                                // Skip Consent Notices in the main notices listing (they're managed separately)
                                if (notice.sourceType === 'CONSENT_NOTICE') {
                                    return null;
                                }

                                const typeColor = getNoticeTypeColor(notice, isDark);

                                return (
                                    <TableRow
                                        key={notice.NoticeId}
                                        hover
                                        onClick={() => {
                                            if (notice.NoticeId) {
                                                setNoticeToEdit(notice.NoticeId);
                                                setNoticeModalOpen(true);
                                            }
                                        }}
                                        sx={{ cursor: 'pointer' }}
                                    >
                                        <TableCell>
                                            <Chip
                                                label={getNoticeTypeLabel(notice)}
                                                size="small"
                                                sx={{
                                                    bgcolor: typeColor.bg,
                                                    color: typeColor.color,
                                                    border: `1px solid ${typeColor.border}`,
                                                    fontWeight: 500
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {notice.Title}
                                            </Typography>
                                            {notice.Description && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {notice.Description.substring(0, 60)}
                                                    {notice.Description.length > 60 ? '...' : ''}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        {/* Show assignment columns when filtering by DP or assignment fields */}
                                        {(filters.dpId || filters.assignmentStatus || filters.isAcknowledged !== undefined || filters.emailSent !== undefined || filters.smsSent !== undefined) ? (
                                            <>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {notice.DPIdentifier || (notice.DPId ? `DP #${notice.DPId}` : '-')}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={notice.AssignmentStatus || notice.Status}
                                                        size="small"
                                                        color={(notice.AssignmentStatus || notice.Status) === 'ACTIVE' ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                                <TableCell>
                                                    {notice.IsAcknowledged ? (
                                                        <Tooltip title={notice.AcknowledgedDate ? `Acknowledged on ${formatFriendlyDate(notice.AcknowledgedDate)}` : 'Acknowledged'}>
                                                            <Chip
                                                                icon={<CheckCircleIcon />}
                                                                label="Yes"
                                                                size="small"
                                                                color="success"
                                                            />
                                                        </Tooltip>
                                                    ) : (
                                                        <Chip
                                                            icon={<CancelIcon />}
                                                            label="No"
                                                            size="small"
                                                            color="default"
                                                        />
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {notice.EffectiveDate ? formatFriendlyDate(notice.EffectiveDate) : '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                                                        {notice.EmailSent && (
                                                            <Tooltip title="Email sent">
                                                                <EmailIcon fontSize="small" color="success" />
                                                            </Tooltip>
                                                        )}
                                                        {notice.SmsSent && (
                                                            <Tooltip title="SMS sent">
                                                                <SmsIcon fontSize="small" color="success" />
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </>
                                        ) : (
                                            <>
                                                <TableCell>
                                                    <Typography variant="body2">
                                                        {notice.TotalAssignments !== undefined ? (
                                                            <>
                                                                {notice.TotalAssignments} total
                                                                {notice.AcknowledgedCount !== undefined && notice.UnacknowledgedCount !== undefined && (
                                                                    <Typography variant="caption" color="text.secondary" display="block">
                                                                        {notice.AcknowledgedCount} acknowledged, {notice.UnacknowledgedCount} pending
                                                                    </Typography>
                                                                )}
                                                            </>
                                                        ) : '-'}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        label={notice.Status}
                                                        size="small"
                                                        color={notice.Status === 'ACTIVE' ? 'success' : 'default'}
                                                    />
                                                </TableCell>
                                            </>
                                        )}
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatFriendlyDate(notice.CreatedDate)}
                                            </Typography>
                                            {notice.CreatedByName && (
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    by {notice.CreatedByName}
                                                </Typography>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
                <TablePagination
                    component="div"
                    count={totalCount}
                    page={page}
                    onPageChange={handleChangePage}
                    rowsPerPage={pageSize}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={[10, 20, 50, 100]}
                />
            </TableContainer>

            {/* Create/Edit Notice Modal */}
            <NoticeModal
                open={noticeModalOpen}
                onClose={() => {
                    setNoticeModalOpen(false);
                    setNoticeToEdit(null);
                }}
                onSave={() => {
                    loadNotices();
                }}
                noticeId={noticeToEdit || undefined}
            />

            {/* Filter Modal */}
            <Dialog
                open={isFilterModalOpen}
                onClose={() => setIsFilterModalOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Filters</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
                        {/* Notice Type Filter */}
                        <FormControl fullWidth>
                            <InputLabel>Notice Type</InputLabel>
                            <Select
                                value={filters.noticeType || ''}
                                label="Notice Type"
                                onChange={(e) => handleFilterChange('noticeType', e.target.value)}
                            >
                                <MenuItem value="">All Types</MenuItem>
                                <MenuItem value="BREACH_NOTICE">Breach</MenuItem>
                                <MenuItem value="ERASURE_NOTICE">Erasure</MenuItem>
                                <MenuItem value="INFORMATIONAL_NOTICE">Informational</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Status Filter */}
                        <FormControl fullWidth>
                            <InputLabel>Notice Status</InputLabel>
                            <Select
                                value={filters.status || ''}
                                label="Notice Status"
                                onChange={(e) => handleFilterChange('status', e.target.value)}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="ARCHIVED">Archived</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Assignment Status Filter */}
                        <FormControl fullWidth>
                            <InputLabel>Assignment Status</InputLabel>
                            <Select
                                value={filters.assignmentStatus || ''}
                                label="Assignment Status"
                                onChange={(e) => handleFilterChange('assignmentStatus', e.target.value)}
                            >
                                <MenuItem value="">All Statuses</MenuItem>
                                <MenuItem value="ACTIVE">Active</MenuItem>
                                <MenuItem value="ARCHIVED">Archived</MenuItem>
                            </Select>
                        </FormControl>

                        {/* Acknowledged Filter */}
                        <FormControl fullWidth>
                            <InputLabel>Acknowledged</InputLabel>
                            <Select
                                value={filters.isAcknowledged === undefined ? '' : filters.isAcknowledged.toString()}
                                label="Acknowledged"
                                onChange={(e) => handleFilterChange('isAcknowledged', e.target.value === '' ? undefined : e.target.value === 'true')}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="true">Yes</MenuItem>
                                <MenuItem value="false">No</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={clearFilters} color="inherit">
                        Clear All
                    </Button>
                    <Button onClick={() => setIsFilterModalOpen(false)}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setIsFilterModalOpen(false);
                            loadNotices();
                        }}
                    >
                        Apply
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Notices;

